import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { firestore } from "services/firebase/firebase";
import { firebaseAuthService } from "services/firebase/firebaseAuth";
import { store } from "redux/store";
import {
  setChannels,
  updateChannel,
  setChannelConnectionState,
} from "redux/actions";
import ChannelService from "services/channel/ChannelService";
import { SUBDOMAIN } from "services/Auth";

const CHANNEL_NOTIFICATIONS_AFTER_KEY = "channel_notifications_after_timestamp";
const NOTIFICATIONS_AFTER_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const NOTIFICATION_SOUND_URL = "/sounds/bip.mp3";
const FIRESTORE_BATCH_SIZE = 10;

function playNotificationSound() {
  if (typeof Audio === "undefined") return;
  try {
    const audio = new Audio(NOTIFICATION_SOUND_URL);
    audio.volume = 0.3;
    audio.play().catch(() => {});
  } catch (e) {}
}

function normalizeDate(date) {
  if (!date) return "";
  if (typeof date === "string") return date;
  if (typeof date === "number") return new Date(date).toISOString();
  if (date && typeof date === "object" && typeof date.toDate === "function")
    return date.toDate().toISOString();
  if (date && typeof date === "object" && typeof date.seconds === "number")
    return new Date(date.seconds * 1000).toISOString();
  if (typeof date === "object" && date !== null) return "[unknown]";
  return String(date);
}

function getNotificationStartTimestamp() {
  try {
    const stored = localStorage.getItem(CHANNEL_NOTIFICATIONS_AFTER_KEY);
    if (stored) {
      const storedTime = new Date(stored).getTime();
      if (
        Number.isFinite(storedTime) &&
        Date.now() - storedTime < NOTIFICATIONS_AFTER_MAX_AGE_MS
      ) {
        return new Date(storedTime + 1000).toISOString();
      }
    }
  } catch (e) {}
  return new Date(Date.now() - 60000).toISOString();
}

function processNewChannelNotification(data, userId, dispatch) {
  const channelId = String(data.channelId);
  if (!data.lastMessage) {
    if (channelId) {
      dispatch(updateChannel({ id: channelId, lastMessage: null }));
    }
    return;
  }

  const currentChannels = store.getState().channelState.channels;
  const currentChannel = currentChannels.find(
    (c) => String(c.id) === channelId
  );

  const currentSentAt = normalizeDate(currentChannel?.lastMessage?.sentAt);
  const newSentAt = normalizeDate(data.lastMessage.sentAt);
  const isSameMessage =
    currentSentAt === newSentAt &&
    currentChannel?.lastMessage?.data === data.lastMessage.data;

  try {
    localStorage.setItem(CHANNEL_NOTIFICATIONS_AFTER_KEY, newSentAt);
  } catch (e) {}

  if (isSameMessage) return;

  const isCurrentUserAuthor = String(data.lastMessage.sentBy) === userId;
  const isViewingChannel =
    typeof window !== "undefined" &&
    window.location.pathname.includes(`/channel/${channelId}`);
  const isWindowHidden =
    typeof document !== "undefined" && document.hidden;
  const shouldIncrementUnread = !isViewingChannel && !isCurrentUserAuthor;
  const currentUnread = currentChannel?.unread ?? 0;
  const newUnread = shouldIncrementUnread ? currentUnread + 1 : currentUnread;

  dispatch(
    updateChannel({
      id: channelId,
      lastMessage: data.lastMessage,
      unread: newUnread,
    })
  );

  const shouldNotify =
    !isCurrentUserAuthor && (!isViewingChannel || isWindowHidden);
  if (!shouldNotify) return;

  playNotificationSound();

  const title = `${data.senderInfo?.name || "Alguém"} em #${data.channelName || "chat"}`;
  if (
    typeof Notification !== "undefined" &&
    Notification.permission === "granted"
  ) {
    const n = new Notification(title, {
      body: data.lastMessage.data,
      icon: data.senderInfo?.img,
      tag: String(data.channelId),
    });
    n.onclick = () => {
      if (data.channelUrl) window.location.href = data.channelUrl;
    };
  } else if (
    typeof Notification !== "undefined" &&
    Notification.permission !== "denied"
  ) {
    Notification.requestPermission();
  }
}

function mergeChannelsWithStored(apiChannels, stored) {
  return apiChannels.map((apiChannel) => {
    const storedChannel = stored.find(
      (c) => String(c.id) === String(apiChannel.id)
    );
    const unread =
      Number(apiChannel.unread) || Number(apiChannel.unread_count) || 0;
    const lastMessage =
      apiChannel.lastMessage ?? storedChannel?.lastMessage;
    return {
      ...apiChannel,
      id: String(apiChannel.id),
      unread,
      ...(lastMessage && { lastMessage }),
    };
  });
}

function unlockAudioOnce() {
  if (typeof Audio === "undefined") return;
  try {
    const audio = new Audio(NOTIFICATION_SOUND_URL);
    audio.volume = 0;
    audio.play().catch(() => {});
  } catch (e) {}
}

export function useLegacyChannelNotificationListener(options = {}) {
  const { enabled = true } = options;
  const dispatch = useDispatch();
  const channels = useSelector((state) => state.channelState.channels);
  const teamConfig = useSelector((state) => state.teamState?.config);
  const user = useSelector((state) => state.userState?.profile);
  const subdomain = teamConfig?.subdomain || SUBDOMAIN || "dev";
  const userId = user?.id != null ? String(user.id) : null;
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [startTimestamp] = useState(getNotificationStartTimestamp);
  const audioUnlockedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    if (audioUnlockedRef.current) return;
    const unlock = () => {
      if (audioUnlockedRef.current) return;
      audioUnlockedRef.current = true;
      if (typeof document !== "undefined") {
        document.removeEventListener("click", unlock);
        document.removeEventListener("touchstart", unlock);
      }
      unlockAudioOnce();
    };
    if (typeof document !== "undefined") {
      document.addEventListener("click", unlock, {
        once: true,
        passive: true,
      });
      document.addEventListener("touchstart", unlock, {
        once: true,
        passive: true,
      });
    }
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("click", unlock);
        document.removeEventListener("touchstart", unlock);
      }
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let retryTimeoutId = null;
    const RETRY_DELAY_MS = 10000;
    const MAX_RETRY_DELAY_MS = 60000;

    function attemptAuth(nextDelayMs = RETRY_DELAY_MS) {
      if (cancelled) return;
      firebaseAuthService.authenticateWithCustomToken().then((ok) => {
        if (cancelled) return;
        setFirebaseReady(ok);
        if (!ok) {
          console.warn(
            "Firebase auth falhou - listeners Firestore desativados, nova tentativa em breve"
          );
          retryTimeoutId = setTimeout(
            () => attemptAuth(Math.min(nextDelayMs + 10000, MAX_RETRY_DELAY_MS)),
            nextDelayMs
          );
        }
      });
    }

    attemptAuth();
    return () => {
      cancelled = true;
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (
      !firebaseReady ||
      !firestore ||
      !subdomain ||
      !userId ||
      !channels ||
      channels.length === 0
    ) {
      return;
    }

    const channelIds = channels
      .map((c) => Number(c.id))
      .filter((id) => !Number.isNaN(id));
    if (channelIds.length === 0) return;

    const unsubscribes = [];

    for (let i = 0; i < channelIds.length; i += FIRESTORE_BATCH_SIZE) {
      const batch = channelIds.slice(i, i + FIRESTORE_BATCH_SIZE);
      const q = query(
        collection(firestore, `chat_rooms/${subdomain}/channels`),
        where("channelId", "in", batch),
        where("createdAt", ">=", startTimestamp)
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            if (change.type !== "added") return;
            const data = change.doc.data();
            processNewChannelNotification(data, userId, dispatch);
          });
        },
        (err) => {
          console.warn("Firestore channel listener error:", err);
        }
      );
      unsubscribes.push(unsubscribe);
    }

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- channels.length evita re-subscribe a cada updateChannel (nova ref do array)
  }, [
    enabled,
    firebaseReady,
    channels.length,
    subdomain,
    userId,
    startTimestamp,
    dispatch,
  ]);
}

export function useChannelSync(options = {}) {
  const { enabled = true } = options;
  const dispatch = useDispatch();
  const channels = useSelector((state) => state.channelState.channels);
  const loading = useSelector(
    (state) => state.channelState?.isLoading ?? false
  );
  const channelSyncRunRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      dispatch(setChannelConnectionState({ isLoading: false }));
      return;
    }
    const runId = ++channelSyncRunRef.current;
    let cancelled = false;
    dispatch(setChannelConnectionState({ isLoading: true }));
    ChannelService.getChannels()
      .then((res) => {
        if (cancelled || runId !== channelSyncRunRef.current) return;
        const apiChannels = res.data || [];
        const stored = store.getState().channelState.channels;
        const merged = mergeChannelsWithStored(apiChannels, stored);
        dispatch(setChannels(merged));
        dispatch(
          setChannelConnectionState({
            isConnected: true,
            isLoading: false,
          })
        );
      })
      .catch((err) => {
        if (cancelled || runId !== channelSyncRunRef.current) return;
        console.warn("Erro ao carregar canais:", err);
        dispatch(
          setChannelConnectionState({
            isConnected: false,
            isLoading: false,
          })
        );
      });
    return () => {
      cancelled = true;
      if (runId === channelSyncRunRef.current) {
        dispatch(setChannelConnectionState({ isLoading: false }));
      }
      channelSyncRunRef.current += 1;
    };
  }, [dispatch, enabled]);

  useLegacyChannelNotificationListener({ enabled });

  return { channels, loading };
}
